import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { getLocationReturnPath, openRecord, useNavigateBack } from '../../hooks/useNavigateBack';
import { cn } from '../../lib/utils';
import { Button, Label, Select, Textarea } from '../../components/ui';
import { fetcher } from '../../services/fetcher';
import {
  createMgaPeriod,
  generateMgaPeriod,
  importMgaFromPrior,
} from '../../services/mutations';
import { moduleConfig } from './config';

const MONTHS = [
  { value: '0', label: 'January' },
  { value: '1', label: 'February' },
  { value: '2', label: 'March' },
  { value: '3', label: 'April' },
  { value: '4', label: 'May' },
  { value: '5', label: 'June' },
  { value: '6', label: 'July' },
  { value: '7', label: 'August' },
  { value: '8', label: 'September' },
  { value: '9', label: 'October' },
  { value: '10', label: 'November' },
  { value: '11', label: 'December' },
];

const CURRENT_UTC_YEAR = new Date().getUTCFullYear();
const CURRENT_UTC_MONTH = String(new Date().getUTCMonth());

function monthRangeIso(year: number, monthIndex: number) {
  const from = new Date(Date.UTC(year, monthIndex, 1)).toISOString().slice(0, 10);
  const to = new Date(Date.UTC(year, monthIndex + 1, 0)).toISOString().slice(0, 10);
  return { from, to };
}

function errText(err: unknown, fallback: string) {
  const msg =
    (err as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
  return Array.isArray(msg) ? msg.join(' ') : typeof msg === 'string' ? msg : fallback;
}

type Step = 1 | 2 | 3 | 4;

const Form: React.FC<{ lockedVesselId?: number }> = ({ lockedVesselId }) => {
  const { colors } = useTheme();
  const { showError, showSuccess } = useNotification();
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getLocationReturnPath(location.state);
  const goBack = useNavigateBack(moduleConfig.module.route.list, returnPath);
  const canEdit =
    can(moduleConfig.module.permissionModuleKey, 'edit') || can('vessel_mga', 'edit');

  const { data: lookups } = useSWR<{
    vessels: Array<{ id: number; vesselName: string }>;
    currencies: Array<{ id: number; code: string; name: string }>;
  }>('/crew/lookups', fetcher, { revalidateOnFocus: false });

  const [step, setStep] = useState<Step>(1);
  const [vesselId, setVesselId] = useState(lockedVesselId ? String(lockedVesselId) : '');
  const [year, setYear] = useState(String(CURRENT_UTC_YEAR));
  const [month, setMonth] = useState(CURRENT_UTC_MONTH);
  const [currency, setCurrency] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [periodId, setPeriodId] = useState<number | null>(null);
  const [generatedCount, setGeneratedCount] = useState<number | null>(null);
  const [importSummary, setImportSummary] = useState<string | null>(null);
  const [skipImport, setSkipImport] = useState(false);

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );
  const currencyOptions = useMemo(
    () =>
      (lookups?.currencies ?? []).map((c) => ({
        value: c.code,
        label: c.name,
      })),
    [lookups?.currencies],
  );
  const yearOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string }> = [];
    for (let i = CURRENT_UTC_YEAR + 1; i >= CURRENT_UTC_YEAR - 3; i -= 1) {
      opts.push({ value: String(i), label: String(i) });
    }
    return opts;
  }, []);

  const range = useMemo(
    () => monthRangeIso(Number(year) || CURRENT_UTC_YEAR, Number(month) || 0),
    [year, month],
  );

  const steps: Array<{ n: Step; label: string }> = [
    { n: 1, label: 'Period' },
    { n: 2, label: 'Create' },
    { n: 3, label: 'Crew' },
    { n: 4, label: 'Import prior' },
  ];

  const createPeriod = async () => {
    if (!canEdit) return;
    const vid = lockedVesselId ?? Number(vesselId);
    if (!vid) {
      showError('Select a vessel.');
      return;
    }
    setBusy(true);
    try {
      const created = await createMgaPeriod({
        vesselId: vid,
        periodFrom: range.from,
        periodTo: range.to,
        currency: currency || null,
        notes: notes.trim() || null,
      });
      setPeriodId(created.id);
      showSuccess('MGA period created.');
      setStep(3);
    } catch (err: unknown) {
      showError(errText(err, 'Create failed.'));
    } finally {
      setBusy(false);
    }
  };

  const generateCrew = async () => {
    if (!periodId || !canEdit) return;
    setBusy(true);
    try {
      const res = await generateMgaPeriod(periodId);
      const n = res.generatedCount ?? res.lines?.length ?? 0;
      setGeneratedCount(n);
      showSuccess(n ? `Generated ${n} crew line${n === 1 ? '' : 's'}.` : 'No new crew lines to add.');
      setStep(4);
    } catch (err: unknown) {
      showError(errText(err, 'Generate failed.'));
    } finally {
      setBusy(false);
    }
  };

  const runImport = async () => {
    if (!periodId || !canEdit) return;
    setBusy(true);
    try {
      const res = await importMgaFromPrior(periodId);
      const parts = [
        res.previousBalancesUpdated
          ? `${res.previousBalancesUpdated} previous balance(s)`
          : null,
        res.bondedStockCopied ? `${res.bondedStockCopied} remaining stock line(s)` : null,
        res.openingCashCreated ? 'opening cash' : null,
      ].filter(Boolean);
      const summary = parts.length
        ? `Imported: ${parts.join(', ')}.`
        : 'Prior CLOSED period found, but nothing new to import.';
      setImportSummary(summary);
      showSuccess(summary);
    } catch (err: unknown) {
      const text = errText(err, 'Import requires a previous CLOSED MGA for this vessel.');
      setImportSummary(text);
      showError(text);
    } finally {
      setBusy(false);
    }
  };

  const finish = () => {
    if (periodId) {
      openRecord(
        navigate,
        moduleConfig.module.route.sheet(periodId),
        returnPath ?? moduleConfig.module.route.list,
      );
    } else {
      goBack();
    }
  };

  const StepPill = ({ n, label }: { n: Step; label: string }) => (
    <div
      className={cn(
        'flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-medium',
        step === n
          ? cn(colors.primary, colors.primaryText)
          : step > n
            ? cn(colors.surfaceHover, colors.text)
            : cn(colors.surfaceHover, colors.textMuted),
      )}
    >
      <span className="tabular-nums">{n}</span>
      <span className="hidden sm:inline">{label}</span>
    </div>
  );

  return (
    <div className={cn('min-h-full', colors.background)}>
      <div className="mx-auto max-w-2xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <Link
            to={moduleConfig.module.route.list}
            className={cn('text-sm font-medium', colors.textSecondary, 'hover:underline')}
          >
            ← Back to MGA
          </Link>
          <h1 className={cn('mt-2 text-2xl font-semibold tracking-tight', colors.text)}>
            New MGA period
          </h1>
          <p className={cn('mt-1 text-sm', colors.textSecondary)}>
            Guided setup: create the month, generate crew from the onboard list, then import
            carry-forward from the previous CLOSED period.
          </p>
        </div>

        <div className="mb-5 flex flex-wrap gap-2">
          {steps.map((s) => (
            <StepPill key={s.n} n={s.n} label={s.label} />
          ))}
        </div>

        <div className={cn('space-y-4 rounded-xl border p-5 shadow-sm', colors.surface, colors.border)}>
          {step === 1 && (
            <>
              <div>
                <Label htmlFor="vesselId" required>
                  Vessel
                </Label>
                {lockedVesselId ? (
                  <p className={cn('mt-1 text-sm font-medium', colors.text)}>
                    {vesselOptions.find((v) => v.value === String(lockedVesselId))?.label ??
                      `Vessel #${lockedVesselId}`}
                  </p>
                ) : (
                  <Select
                    name="vesselId"
                    value={vesselId}
                    onChange={(e) => setVesselId(e.target.value)}
                    options={vesselOptions}
                    placeholder="Select vessel"
                    required
                    showClearButton={false}
                    disabled={!canEdit}
                  />
                )}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="month" required>
                    Month
                  </Label>
                  <Select
                    name="month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                    options={MONTHS}
                    showClearButton={false}
                    disabled={!canEdit}
                  />
                </div>
                <div>
                  <Label htmlFor="year" required>
                    Year
                  </Label>
                  <Select
                    name="year"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                    options={yearOptions}
                    showClearButton={false}
                    disabled={!canEdit}
                  />
                </div>
              </div>
              <p className={cn('text-xs', colors.textMuted)}>
                Period dates: {range.from} → {range.to}
              </p>
              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  name="currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  options={currencyOptions}
                  placeholder="Optional"
                  disabled={!canEdit}
                />
              </div>
              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  name="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  maxLength={250}
                  showCharacterCount
                  disabled={!canEdit}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={goBack}>
                  Cancel
                </Button>
                {canEdit && (
                  <Button
                    type="button"
                disabled={!canEdit || !(lockedVesselId || vesselId)}
                onClick={() => setStep(2)}
              >
                    Continue
                  </Button>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className={cn('text-sm font-semibold', colors.text)}>Confirm & create</h2>
              <dl className={cn('space-y-2 text-sm', colors.textSecondary)}>
                <div className="flex justify-between gap-4">
                  <dt>Vessel</dt>
                  <dd className={colors.text}>
                    {vesselOptions.find((v) => v.value === vesselId)?.label ?? vesselId}
                  </dd>
                </div>
                <div className="flex justify-between gap-4">
                  <dt>Period</dt>
                  <dd className={cn('tabular-nums', colors.text)}>
                    {range.from} → {range.to}
                  </dd>
                </div>
                {currency ? (
                  <div className="flex justify-between gap-4">
                    <dt>Currency</dt>
                    <dd className={colors.text}>{currency}</dd>
                  </div>
                ) : null}
              </dl>
              <div className="flex justify-between gap-2 pt-2">
                <Button type="button" variant="ghost" disabled={busy} onClick={() => setStep(1)}>
                  Back
                </Button>
                {canEdit && (
                  <Button type="button" disabled={busy} onClick={createPeriod}>
                    {busy ? 'Creating…' : 'Create period'}
                  </Button>
                )}
              </div>
            </>
          )}

          {step === 3 && periodId != null && (
            <>
              <h2 className={cn('text-sm font-semibold', colors.text)}>Generate crew lines</h2>
              <p className={cn('text-sm', colors.textSecondary)}>
                Build payroll rows from the vessel crew list for overlapping onboard assignments,
                snapshotting contract wages.
              </p>
              {generatedCount != null && (
                <p className={cn('text-sm', colors.text)}>{generatedCount} line(s) generated.</p>
              )}
              <div className="flex justify-between gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={busy}
                  onClick={() =>
                    periodId
                      ? openRecord(
                          navigate,
                          moduleConfig.module.route.detail(periodId),
                          returnPath ?? moduleConfig.module.route.list,
                        )
                      : goBack()
                  }
                >
                  Skip to dashboard
                </Button>
                {canEdit && (
                  <Button type="button" disabled={busy} onClick={generateCrew}>
                    {busy ? 'Generating…' : 'Generate from crew list'}
                  </Button>
                )}
              </div>
            </>
          )}

          {step === 4 && periodId != null && (
            <>
              <h2 className={cn('text-sm font-semibold', colors.text)}>Import prior month</h2>
              <p className={cn('text-sm', colors.textSecondary)}>
                One click pulls previous balances, remaining bonded stock, and opening cash from the
                latest CLOSED MGA for this vessel.
              </p>
              {importSummary && (
                <p className={cn('rounded-lg border px-3 py-2 text-sm', colors.border, colors.text)}>
                  {importSummary}
                </p>
              )}
              <label className={cn('flex items-center gap-2 text-sm', colors.textSecondary)}>
                <input
                  type="checkbox"
                  checked={skipImport}
                  onChange={(e) => setSkipImport(e.target.checked)}
                  disabled={busy || Boolean(importSummary)}
                />
                Skip import (start empty)
              </label>
              <div className="flex flex-wrap justify-between gap-2 pt-2">
                <Button type="button" variant="ghost" disabled={busy} onClick={() => setStep(3)}>
                  Back
                </Button>
                <div className="flex flex-wrap gap-2">
                  {canEdit && !skipImport && !importSummary && (
                    <Button type="button" disabled={busy} onClick={runImport}>
                      {busy ? 'Importing…' : 'Import prior month'}
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant={importSummary || skipImport ? 'accent' : 'outline'}
                    disabled={busy}
                    onClick={finish}
                  >
                    Open sheet
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Form;
